// Priority SEO page: Is plastic surgery in Vietnam safe?
const safety = {
  slug: "plastic-surgery-safety-vietnam",
  metaTitle: "Is Plastic Surgery in Vietnam Safe? | Patient Guide",
  metaDescription:
    "Learn how surgeon licensing, hospital standards, anaesthesia, preparation and aftercare influence plastic surgery safety in Vietnam.",
  eyebrow: "Safety",
  h1: "Is Plastic Surgery in Vietnam Safe?",
  intro:
    "Safety is not decided by a country. It depends on the individual surgeon, the facility, the anaesthesia team, your own health, the specific procedure and the quality of your aftercare.",
  summary:
    "There is no simple yes or no answer. Standards vary between providers in Vietnam, as they do everywhere. Understanding surgeon licensing, facility standards, anaesthesia, preparation and recovery helps you assess safety for your specific case.",
  published: "December 2025",
  updated: "December 2025",
  breadcrumb: [{ name: "Safety", to: "/plastic-surgery-safety-vietnam" }],
  sections: [
    {
      id: "summary",
      h2: "A balanced summary",
      paragraphs: [
        "Plastic surgery in Vietnam can be performed to a high standard by experienced, licensed surgeons in appropriate facilities. It can also be performed poorly by under-qualified providers in unsuitable settings.",
        "The useful question is not \"is Vietnam safe?\" but \"is this surgeon, in this facility, appropriate for my procedure?\"",
      ],
    },
    {
      id: "provider",
      h2: "Safety depends on the provider, not only the country",
      paragraphs: [
        "The single biggest influence on safety is the team performing your surgery and where they perform it. Focus your assessment on the specifics below rather than national generalisations.",
      ],
      subsections: [
        { h3: "Surgeon licensing", paragraphs: ["Confirm the surgeon is licensed to perform the procedure and ask how you can verify this."] },
        { h3: "Procedure-specific experience", paragraphs: ["Experience with your exact procedure matters more than general experience. Ask how often they perform it."] },
        { h3: "Hospital and operating-location standards", paragraphs: ["Ask whether surgery takes place in a licensed hospital or an appropriate facility, and what monitoring is available."] },
        { h3: "Anaesthesia and the anaesthetist", paragraphs: ["A qualified anaesthetist and appropriate monitoring are essential, particularly for longer procedures under general anaesthesia."] },
      ],
    },
    {
      id: "preparation",
      h2: "Preparation and prevention",
      subsections: [
        { h3: "Preoperative health screening", paragraphs: ["Blood tests and a health review help identify risks before surgery. Be honest about your medical history and medication."] },
        { h3: "Infection prevention", paragraphs: ["Sterile technique, appropriate antibiotics where indicated and clean facilities reduce infection risk."] },
        { h3: "Informed consent", paragraphs: ["You should understand the risks, limitations and alternatives, and never feel rushed into a decision."] },
        { h3: "Emergency readiness", paragraphs: ["Ask how the facility manages complications and whether emergency care is available."] },
      ],
    },
    {
      id: "devices",
      h2: "Medical devices and implants",
      paragraphs: [
        "Where implants or devices are used, ask for documentation of the exact product. Verified product information supports both safety and any future follow-up or revision.",
      ],
    },
    {
      id: "recovery",
      h2: "Recovery, monitoring and travelling too soon",
      paragraphs: [
        "Recovery is part of safety. Adequate monitoring after surgery and a sensible plan for follow-up reduce risk.",
        "Travelling home too soon can be dangerous. Flying should only take place once your surgeon has given travel clearance, as long flights can increase certain risks after surgery.",
      ],
    },
    {
      id: "warning-signs",
      h2: "Warning signs after surgery",
      bullets: [
        "Increasing pain, redness, swelling or heat around the surgical area",
        "Fever or feeling generally unwell",
        "Unusual discharge or a wound that opens",
        "Shortness of breath, chest pain or calf pain or swelling",
        "Any symptom that worries you",
      ],
      callout:
        "If you experience severe or sudden symptoms, seek urgent medical care immediately and contact your surgeon.",
    },
    {
      id: "questions",
      h2: "Questions patients should ask",
      bullets: [
        "Are you licensed for this procedure, and how can I verify it?",
        "Where will surgery take place, and is the facility licensed?",
        "Who provides the anaesthesia, and how am I monitored?",
        "How are complications handled?",
        "What follow-up is available if I travel home?",
      ],
    },
    {
      id: "what-we-check",
      h2: "What Plastic Surgery Vietnam checks — and what it cannot guarantee",
      paragraphs: [
        "As an independent coordination platform, we help review provider licensing, confirm the operating location and organise direct consultations so you can make an informed decision.",
        "We are not a clinic or medical provider. We cannot guarantee an outcome, and all clinical decisions are made by licensed medical professionals.",
      ],
    },
  ],
  faqs: [
    { q: "Is plastic surgery in Vietnam safe?", a: "It can be, when performed by a licensed, experienced surgeon in an appropriate facility with proper anaesthesia and aftercare. Standards vary between providers, so safety should be assessed for your specific case." },
    { q: "How do I know a facility is suitable?", a: "Ask whether surgery takes place in a licensed hospital or appropriate facility, what monitoring is available and how complications are handled." },
    { q: "When can I fly home after surgery?", a: "Only once your surgeon gives travel clearance. Flying too soon can increase certain risks after surgery." },
    { q: "Can you guarantee my surgery will be safe?", a: "No. No responsible party guarantees safety or outcomes. We help you gather information and organise consultations so a licensed professional can assess your case." },
  ],
  sources: [
    { label: "International Society of Aesthetic Plastic Surgery (ISAPS)", url: "https://www.isaps.org/" },
    { label: "NHS — Cosmetic procedures: things to consider", url: "https://www.nhs.uk/conditions/cosmetic-procedures/" },
  ],
  relatedTitle: "Related guides & procedures",
  related: [
    { to: "/how-to-choose-a-plastic-surgeon-in-vietnam", title: "How to choose a plastic surgeon", desc: "Licensing, experience, red flags" },
    { to: "/plastic-surgery-cost-vietnam", title: "Plastic surgery costs in Vietnam", desc: "What a quotation includes" },
    { to: "/patient-journey", title: "The patient journey", desc: "What to expect before you travel" },
    { to: "/revision-plastic-surgery-vietnam", title: "Revision plastic surgery", desc: "Corrective and complex cases" },
  ],
};

export default safety;
